import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cloud, Upload, Share2, Github, Heart } from 'lucide-react';

export function Layout({ children }) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-2 text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Cloud className="h-6 w-6 text-primary-600" />
              </div>
              <span>Wasabi Storage</span>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-6">
              <Link 
                to="/" 
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isHomePage 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Upload className="h-4 w-4" />
                <span>Upload</span>
              </Link>
              
              <a 
                href="https://github.com/your-username/wasabi-storage" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <Github className="h-4 w-4" />
                <span>GitHub</span>
              </a>
            </nav>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Cloud className="h-5 w-5 text-primary-600" />
                </div>
                <span className="font-semibold text-gray-900">Wasabi Storage</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Secure, temporary file sharing with automatic expiration. Built with Wasabi S3 storage and modern web technologies.
              </p>
              <div className="flex items-center space-x-1 text-sm text-gray-600">
                <span>Made with</span>
                <Heart className="h-4 w-4 text-red-500 fill-current" />
                <span>for secure file sharing</span>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Features</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center space-x-2">
                  <div className="h-1.5 w-1.5 bg-primary-500 rounded-full"></div>
                  <span>Secure file uploads</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="h-1.5 w-1.5 bg-primary-500 rounded-full"></div>
                  <span>Automatic expiration</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="h-1.5 w-1.5 bg-primary-500 rounded-full"></div>
                  <span>Password protection</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="h-1.5 w-1.5 bg-primary-500 rounded-full"></div>
                  <span>Download tracking</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Tech Stack</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>React + Vite</li>
                <li>Tailwind CSS</li>
                <li>Node.js + Express</li>
                <li>Wasabi S3 Storage</li>
                <li>SQLite + Prisma</li>
                <li>Cloudflare CDN</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-600">
              © 2024 Wasabi Storage. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <a 
                href="https://github.com/your-username/wasabi-storage" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Github className="h-5 w-5" />
              </a>
              <a 
                href="https://wasabisys.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Powered by Wasabi
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
} 